import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StripeService, OpsSubscriptionModalService, SubscriptionStatus, UserSubscription, LocalStorageService } from '@ops/shared-types';
import { Subscription } from 'rxjs';

export interface PaymentMethod {
  _id?: string;
  id?: string;
  type: string;
  last4?: string;
  brand?: string;
  exp_month?: number;
  exp_year?: number;
  isDefault?: boolean;
  status?: string;
}

@Component({
  selector: 'ops-payment-methods-settings',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './ops-payment-methods-settings.component.html',
  styleUrl: './ops-payment-methods-settings.component.scss',
})
export class OpsPaymentMethodsSettingsComponent implements OnInit, OnDestroy {
  @Input() selectedSubscriptionData: UserSubscription | null = null;
  @Output() closeModal = new EventEmitter<void>();
  @Output() paymentMethodSelected = new EventEmitter<PaymentMethod>();
  @Output() updateDefaultPaymentMethodForSubscription = new EventEmitter<void>();

  paymentMethods: PaymentMethod[] = [];
  selectedPaymentMethodId: string | null = null;
  defaultPaymentMethodId: string | null = null;
  loading = false;
  isLoading = false;
  error: string | null = null;

  private subs: Subscription[] = [];

  constructor(
    private stripeService: StripeService,
    private translateService: TranslateService,
    private modalService: OpsSubscriptionModalService,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.defaultPaymentMethodId = this.selectedSubscriptionData?.defaultPaymentMethodId || null;
    this.loadPaymentMethods();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  loadPaymentMethods(): void {
    this.loading = true;
    this.error = null;

    const sub = this.stripeService.getStripePaymentMethods().subscribe({
      next: (response: any) => {
        this.paymentMethods = response.map((pm: any) => ({
          ...pm,
          isDefault: pm.id === this.defaultPaymentMethodId,
        }));
      },
      error: (error) => {
        console.error('Error getting payment methods:', error);
        this.error = this.translateService.instant('subscription.paymentMethodError');
      },
      complete: () => {
        this.loading = false;
      }
    });
    this.subs.push(sub);
  }

  onPaymentMethodSelect(paymentMethod: PaymentMethod): void {
    this.selectedPaymentMethodId = paymentMethod.id || paymentMethod._id || null;
  }

  onAddPaymentMethod(): void {
    // Redirect to Stripe to add a new payment method
    const sub = this.stripeService.stripeAddPaymentMethod(this.selectedSubscriptionData?.stripeSubscriptionId || '').subscribe({
      next: (response: any) => {
        if (response && response.url) {
          window.open(response.url, '_self');
        } else {
          console.error('No URL received from Stripe service');
        }
      },
      error: (error) => {
        console.error('Error getting payment method URL:', error);
        this.error = this.translateService.instant('subscription.paymentMethodError');
      }
    });
    this.subs.push(sub);
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onSetDefaultPaymentMethod(): void {
    if (!this.selectedPaymentMethodId) {
      return;
    }

    // Find the selected payment method to get the Stripe ID
    const selectedPaymentMethod = this.paymentMethods.find(
      pm => (pm.id || pm._id) === this.selectedPaymentMethodId
    );

    if (!selectedPaymentMethod || !selectedPaymentMethod.id) {
      console.error('Selected payment method not found or missing Stripe ID');
      return;
    }

    if (!this.selectedSubscriptionData?.stripeSubscriptionId) {
      console.error('Subscription ID is required');
      this.error = this.translateService.instant('subscription.paymentMethodError');
      return;
    }

    this.isLoading = true;
    this.error = null;

    const setDefaultSub = this.stripeService.setStripePaymentMethodDefault(selectedPaymentMethod.id, this.selectedSubscriptionData?.stripeSubscriptionId).subscribe({
      next: () => {
        this.isLoading = false;
        const modalSub = this.modalService.openModal({
          statusType: SubscriptionStatus.SUCCESS,
          modalTitle: this.translateService.instant('subscription.paymentMethodSetDefaultSuccessTitle'),
          modalText: this.translateService.instant('subscription.paymentMethodSetDefaultSuccess'),
        }).subscribe();
        this.subs.push(modalSub);
        // Reload payment methods to update the default status
        // this.loadPaymentMethods();
        
        // Update the subscription in localStorageService
        if (this.selectedSubscriptionData?._id) {
          const updateSub = this.stripeService.getUserSubscriptionById(this.selectedSubscriptionData._id).subscribe({
            next: (updatedSubscription: any) => {
              const allSubscriptions = this.localStorageService.getAllUserSubscriptions();
              const updatedSubscriptions = allSubscriptions.map((sub: any) => {
                if (sub._id === updatedSubscription._id) {
                  return updatedSubscription;
                }
                return sub;
              });
              this.localStorageService.setAllUserSubscriptions(updatedSubscriptions);
            },
            error: (error) => {
              console.error('Error fetching updated subscription:', error);
            }
          });
          this.subs.push(updateSub);
        }
        
        this.updateDefaultPaymentMethodForSubscription.emit();
        this.closeModal.emit();
      },
      error: (error) => {
        console.error('Error setting default payment method:', error);
        this.isLoading = false;
        const errorModalSub = this.modalService.openModal({
          statusType: SubscriptionStatus.ERROR,
          modalTitle: this.translateService.instant('subscription.paymentMethodSetDefaultErrorTitle'),
          modalText: this.translateService.instant('subscription.paymentMethodSetDefaultError') || 'Error setting payment method as default',
        }).subscribe();
        this.subs.push(errorModalSub);
      }
    });
    this.subs.push(setDefaultSub);
  }
}
